import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Building2, Plane, Newspaper, Navigation, ArrowRight, Compass } from "lucide-react";
import api from "../lib/api";
import { IataBadge } from "./common";
import { airlineLogo, pickAirportBg } from "../lib/assets";
import Weather from "./Weather";
import NewsFeed from "./NewsFeed";

const KEY = "aa_location";

export default function LocalPanel() {
  const [loc, setLoc] = useState(null);
  const [airports, setAirports] = useState([]);
  const [airlines, setAirlines] = useState([]);

  useEffect(() => {
    const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; } };
    setLoc(read());
    const onStorage = () => setLoc(read());
    window.addEventListener("storage", onStorage);
    const iv = setInterval(() => setLoc(read()), 2000);
    return () => { window.removeEventListener("storage", onStorage); clearInterval(iv); };
  }, []);

  const lat = loc?.lat, lon = loc?.lon, country = loc?.country;
  useEffect(() => {
    if (lat != null) api.get("/nearby-airports", { params: { lat, lon, limit: 6 } }).then((r) => setAirports(r.data.items)).catch(() => setAirports([]));
    if (country) api.get("/airlines", { params: { country, limit: 6 } }).then((r) => setAirlines(r.data.items)).catch(() => setAirlines([]));
  }, [lat, lon, country]);

  if (!loc) {
    return (
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16" data-testid="local-panel-empty">
        <div className="border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
          <Compass className="w-8 h-8 mx-auto text-primary mb-2" />
          <p className="font-heading font-700 text-lg">See airports, airlines & weather near you</p>
          <p className="text-sm text-muted-foreground mt-1">Use “Set location” in the bar above to personalize this page to your city.</p>
        </div>
      </section>
    );
  }

  const HDR = pickAirportBg(loc.city || "local");
  const place = [loc.city, loc.state, loc.country].filter(Boolean).join(", ");

  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16" data-testid="local-panel">
      <div className="relative border border-zinc-300 overflow-hidden mb-8">
        <div className="absolute inset-0"><img src={HDR} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-emerald-900/75" /></div>
        <div className="relative p-6 lg:p-8 text-white flex flex-col md:flex-row md:items-center gap-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-blue-300 mb-1 flex items-center gap-1"><Navigation className="w-3.5 h-3.5" /> Near you</div>
            <h2 className="font-heading font-900 text-2xl sm:text-3xl">Airports & airlines near {loc.city || "you"}</h2>
            <div className="flex items-center gap-1.5 text-white/80 mt-1 text-sm"><MapPin className="w-4 h-4" />{place}
              {loc.city && (
                <Link
                  to={`/cities/${encodeURIComponent(loc.city)}?${new URLSearchParams({
                    ...(loc.country_code ? { country: loc.country_code } : {}),
                    ...(airports?.[0]?.iso_region ? { region: airports[0].iso_region } : {}),
                  }).toString()}`}
                  className="ml-2 text-blue-300 hover:text-white underline underline-offset-2" data-testid="local-city-link">City guide ›</Link>
              )}
            </div>
          </div>
          {loc.lat != null && <div className="md:ml-auto"><Weather lat={loc.lat} lon={loc.lon} label={loc.city} /></div>}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-900 text-blue-600 text-xl flex items-center gap-2"><Building2 className="w-5 h-5" /> Nearby airports</h3>
              <Link to={`/airports${loc.country_code ? `?country=${loc.country_code}` : ""}`} className="text-sm text-primary hover:underline flex items-center gap-1">All <ArrowRight className="w-4 h-4" /></Link>
            </div>
            {airports.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {airports.map((a) => (
                  <Link key={a.id} to={`/airports/${a.iata_code || a.id}`} data-testid={`local-airport-${a.id}`} className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                    <IataBadge code={a.iata_code} name={a.name} className="h-24" size="text-2xl" />
                    <div className="p-2 text-xs text-muted-foreground flex items-center justify-between"><span className="truncate">{a.municipality}</span><span className="font-mono shrink-0">{a.distance_km} km</span></div>
                  </Link>
                ))}
              </div>
            ) : <p className="text-sm text-muted-foreground">No major airports found near this location.</p>}
          </div>

          {airlines.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-900 text-rose-600 text-xl flex items-center gap-2"><Plane className="w-5 h-5 -rotate-45" /> Airlines in {loc.country}</h3>
                <Link to={`/airlines?country=${encodeURIComponent(loc.country)}`} className="text-sm text-primary hover:underline flex items-center gap-1">All <ArrowRight className="w-4 h-4" /></Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {airlines.map((a) => (
                  <Link key={a.id} to={`/airlines/${a.id}`} data-testid={`local-airline-${a.id}`} className="group border border-zinc-300 bg-white overflow-hidden hover:-translate-y-px hover:border-primary transition-[transform,border-color]">
                    <IataBadge code={a.iata || a.icao} name={a.name} logo={airlineLogo(a.iata)} className="h-24" size="text-2xl" />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <h3 className="font-heading font-900 text-violet-600 text-xl flex items-center gap-2 mb-3"><Newspaper className="w-5 h-5" /> Local aviation news</h3>
          <NewsFeed path={`/news?category=airport&q=${encodeURIComponent([loc.city, loc.state].filter(Boolean).join(" ") || loc.country || "")}&limit=6`} compact />
        </div>
      </div>
    </section>
  );
}
